import React, { useEffect, useMemo, useState } from "react";
import "@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css";
import DatePicker from "@hassanmojab/react-modern-calendar-datepicker";
import { useAuth } from "../utils/auth";
import { sdkWrapperURL } from "../utils/api-url-list";

const formatDate = (date, type = "display") =>
  type === "api"
    ? `${date.year}-${date.month}-${date.day}`
    : `${date.day}/${date.month}/${date.year}`;

const GenerateReport = () => {
  const {
    getToken,
    userData: { admin_type, email },
  } = useAuth();
  const [accountsList, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState({
    id: "",
    name: "",
    api_key: "",
  });
  const [reportsList, setReports] = useState([]);
  const [selectedReportName, setReportName] = useState("");
  const [startDate, setStartDate] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: 1,
  });
  const [endDate, setEndDate] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
  });
  const dateRange = useMemo(() => {
    var today = new Date();
    const max = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };
    today.setDate(today.getDate() - 90);
    const min = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };
    return { min, max };
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const generateReport = async () => {
    setLoading(true);
    setError("");
    try {
      const api_key =
        admin_type === "SUPERADMIN" ? selectedAccount.api_key : undefined;
      if (typeof api_key === "string" && api_key.length <= 0)
        throw new Error("Please select an Account.");
      if (selectedReportName.length <= 0)
        throw new Error("Please select a Report.");
      const START_DATE = formatDate(startDate, "api"),
        END_DATE = formatDate(endDate, "api");
      if (new Date(START_DATE) > new Date(END_DATE))
        throw new Error("Start date cannot be in future than End Date");
      const token = await getToken();
      const resp = await fetch(sdkWrapperURL("/admin/analytics"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          api_key,
          action: "execute_query",
          analytics_name: selectedReportName,
          analytics_inputs: { START_DATE, END_DATE, EMAIL: email },
          analytics_output_type: "EMAIL",
        }),
      });
      const respJSON = await resp.json();
      if (respJSON?.statusCode?.toString().startsWith("2")) {
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 3000);
      } else throw new Error(respJSON?.message ?? "Failed to Generate Report.");
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getAccountsList = async () => {
      setLoading(true);
      setAccounts([]);
      setSelectedAccount({ id: "", name: "", api_key: "" });
      try {
        const token = await getToken();
        const listClientsResp = await fetch(sdkWrapperURL("/admin/analytics"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({ action: "list_clients" }),
        });
        const listClientsRespJSON = await listClientsResp.json();
        if (listClientsRespJSON?.statusCode?.toString().startsWith("2")) {
          const accounts = listClientsRespJSON.profile.map((r) => ({
            id: r.client_id,
            name: r.display_name,
            api_key: r.api_key,
          }));
          setAccounts(accounts);
          setSelectedAccount(accounts[0]);
        } else
          throw new Error(
            listClientsRespJSON?.message ?? "Failed to get Clients List"
          );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (admin_type === "SUPERADMIN") getAccountsList();
  }, [admin_type, getToken]);

  useEffect(() => {
    const getReportsList = async (api_key) => {
      setLoading(true);
      setReports([]);
      setReportName("");
      try {
        const token = await getToken();
        const listReportsResp = await fetch(sdkWrapperURL("/admin/analytics"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({ api_key, action: "list_analytics" }),
        });
        const listReportsRespJSON = await listReportsResp.json();
        if (listReportsRespJSON?.statusCode?.toString().startsWith("2")) {
          const reports = listReportsRespJSON.response.map((r) => ({
            id: r.analytics_id,
            name: r.analytics_name,
          }));
          setReports(reports);
          setReportName(reports[0].name);
        } else
          throw new Error(
            listReportsRespJSON?.message ?? "Failed to get Reports"
          );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (admin_type === "SUPERADMIN") {
      if (selectedAccount?.api_key?.length > 0)
        getReportsList(selectedAccount.api_key);
    } else getReportsList();
  }, [selectedAccount.api_key, admin_type, getToken]);

  return (
    <>
      <h1 className="mb-6 text-primary text-2xl font-semibold">
        Generate Report
      </h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          generateReport();
        }}
      >
        {admin_type === "SUPERADMIN" && (
          <div className="mb-4 flex items-center gap-1">
            <label htmlFor="select-client" className="text-darkgray text-sm">
              Select Account:
            </label>
            <select
              className="drop-down pl-3 py-1.5 pr-8 rounded-full outline-none border border-lightgray/50 text-primary text-sm font-medium"
              id="select-client"
              name="select-client"
              value={selectedAccount.id}
              onChange={(e) =>
                setSelectedAccount(
                  accountsList.find((a) => a.id === e.target.value)
                )
              }
              disabled={loading}
            >
              <option value="" hidden disabled>
                Select Account
              </option>
              {accountsList.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <label htmlFor="select-report" className="text-darkgray text-sm">
          Please select the type of report you want to generate
        </label>
        <select
          className="drop-down block w-80 mt-3 mb-6 pl-4 py-2 pr-10 rounded-lg outline-none border border-lightgray/50 text-primary text-sm"
          id="select-report"
          name="select-report"
          value={selectedReportName}
          onChange={(e) => setReportName(e.target.value)}
          disabled={loading}
        >
          <option value="" hidden disabled>
            Select Report
          </option>
          {reportsList.map((r) => (
            <option key={r.id} value={r.name}>
              {r.name}
            </option>
          ))}
        </select>

        <label className="text-darkgray text-sm">
          Please select a date range&nbsp;
          <span className="text-error">(Maximum 3 Months)</span>
        </label>
        <div className="mt-3 mb-6 space-x-5">
          <DatePicker
            value={startDate}
            onChange={setStartDate}
            formatInputText={() => formatDate(startDate)}
            inputName="start-date"
            inputPlaceholder="Start Date"
            inputClassName="date-input block w-40 !p-2.5 rounded-lg outline-none !border !border-lightgray/50 text-primary !text-sm !text-left"
            wrapperClassName="block"
            shouldHighlightWeekends
            calendarPopperPosition="bottom"
            minimumDate={dateRange.min}
            maximumDate={dateRange.max}
          />
          <DatePicker
            value={endDate}
            onChange={setEndDate}
            formatInputText={() => formatDate(endDate)}
            inputName="end-date"
            inputPlaceholder="End Date"
            inputClassName="date-input block w-40 !p-2.5 rounded-lg outline-none !border !border-lightgray/50 text-primary !text-sm !text-left"
            wrapperClassName="block"
            shouldHighlightWeekends
            calendarPopperPosition="bottom"
            minimumDate={dateRange.min}
            maximumDate={dateRange.max}
          />
        </div>
        <label htmlFor="email" className="text-darkgray text-sm">
          Report will be shared on this Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          inputMode="email"
          className="block w-80 mt-3 mb-6 px-4 py-2 rounded-lg outline-none border border-lightgray/50 bg-white disabled:bg-gray-200 text-primary disabled:text-darkgray/60 text-sm"
          defaultValue={email}
          disabled
        />
        <button
          className="block mt-8 w-80 px-4 py-3 rounded-full outline-none bg-primary disabled:bg-darkgray text-white text-base font-medium"
          type="submit"
          disabled={loading}
        >
          Generate Report
        </button>
        {error.length > 0 && (
          <p className="mt-2.5 text-sm text-error">{error}</p>
        )}
        {showAlert && (
          <p class="mt-5 px-6 py-4 rounded-lg bg-[#64BD40] text-white text-sm font-light">
            Report Generated Successfully. You will soon receive the generated
            file in your registered mail address.
          </p>
        )}
        {admin_type !== "SUPERADMIN" && (
          <p className="mt-5 text-primary text-sm">
            NOTE: You can generate upto Max 5 reports per day.
          </p>
        )}
      </form>
    </>
  );
};

export default GenerateReport;
